import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * ScrollToTop
 *
 * On route change, scrolls to top - UNLESS the URL includes a hash
 * (e.g. `/#pricing`, `/#how`), in which case it scrolls to the target
 * element with a small offset for the sticky header.
 *
 * Cross-page hash targets can lazy-mount (the homepage has a video +
 * dynamic sections that paint after the first frame), so we poll for
 * the element up to `MAX_POLL_MS` before giving up.
 *
 * iter 187.j - fix nav bugs so `#how` and `#pricing` land on their
 * sections regardless of which page the user navigated from.
 */
const HEADER_OFFSET = 96; // sticky header 64-80px + breathing room
const MAX_POLL_MS = 1600;
const POLL_INTERVAL = 60;

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return false;
  const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
  window.scrollTo({ top, behavior: 'smooth' });
  return true;
};

export default function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return undefined;
    }

    const id = hash.slice(1);
    // First attempt on next frame (post-paint).
    let cancelled = false;
    const start = Date.now();

    const tick = () => {
      if (cancelled) return;
      if (scrollToId(id)) return;
      if (Date.now() - start >= MAX_POLL_MS) {
        // Fallback: land at top if the anchor never mounts.
        window.scrollTo(0, 0);
        return;
      }
      setTimeout(tick, POLL_INTERVAL);
    };

    // Kick off after the router has painted the new route.
    const rafId = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [pathname, hash]);

  return null;
}
